// src/services/thumbnailGenerationApi.ts
import axios from 'axios';
import { API_BASE_URL } from '../config/api';

// Helper function to get authentication headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken');
  const headers: Record<string, string> = {
    'Content-Type': 'application/json'
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

// Helper function for FormData requests (no Content-Type header)
const getAuthHeadersForFormData = () => {
  const token = localStorage.getItem('authToken');
  const headers: Record<string, string> = {};
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

export interface ThumbnailGenerationResult {
  projectId: string;
  s3Url: string;
  method: string;
  metadata?: any;
}

export interface ThumbnailGenerationRequest {
  projectId?: string;
  method: 'description' | 'extract_frame' | 'existing_caption' | 'upload' | 'existing_image';
  description?: string;
  existingImageUrl?: string; // For existing_image method
  videoUrl?: string;
  frameTimestamp?: number;
  width?: number;
  height?: number;
  aspectRatio?: string;
  style?: string;
  steps?: number;
  guidance_scale?: number;
  seed?: number;
  negative_prompt?: string;
}

/**
 * Generate thumbnail from text description
 */
export const generateThumbnailFromDescription = async (
  description: string,
  options?: Partial<ThumbnailGenerationRequest> & { title?: string }
): Promise<ThumbnailGenerationResult> => {
  const payload: ThumbnailGenerationRequest & { title?: string } = {
    method: 'description',
    description,
    title: options?.title || 'AI Generated Thumbnail', // Add title for new project creation
    width: 1536,
    height: 864,
    aspectRatio: '16:9',
    style: 'realistic',
    steps: 30,
    guidance_scale: 7.5,
    negative_prompt: 'blurry, low quality, distorted, ugly, bad anatomy',
    ...options
  };

  const response = await axios.post(`${API_BASE_URL}/thumbnail/generate`, payload, {
    headers: getAuthHeaders()
  });
  return response.data.data;
};

/**
 * Generate thumbnail from video frame extraction
 */
export const extractFrameFromVideo = async (
  videoUrl: string,
  frameTimestamp: number,
  options?: Partial<ThumbnailGenerationRequest>
): Promise<ThumbnailGenerationResult> => {
  const payload: ThumbnailGenerationRequest = {
    method: 'extract_frame',
    videoUrl,
    frameTimestamp,
    width: 1536,
    height: 864,
    aspectRatio: '16:9',
    ...options
  };

  const response = await axios.post(`${API_BASE_URL}/thumbnail/generate`, payload, {
    headers: getAuthHeaders()
  });
  return response.data.data;
};

/**
 * Generate thumbnail from existing project captions
 */
export const generateThumbnailFromCaptions = async (
  projectId: string,
  options?: Partial<ThumbnailGenerationRequest>
): Promise<ThumbnailGenerationResult> => {
  const payload: ThumbnailGenerationRequest = {
    projectId,
    method: 'existing_caption',
    width: 1536,
    height: 864,
    aspectRatio: '16:9',
    style: 'realistic',
    steps: 30,
    guidance_scale: 7.5,
    negative_prompt: 'blurry, low quality, distorted, ugly, bad anatomy',
    ...options
  };

  const response = await axios.post(`${API_BASE_URL}/thumbnail/generate`, payload, {
    headers: getAuthHeaders()
  });
  return response.data.data;
};

/**
 * Upload image for direct thumbnail editing
 */
export const uploadThumbnailImage = async (
  file: File,
  projectId?: string
): Promise<ThumbnailGenerationResult> => {
  const formData = new FormData();
  formData.append('image', file);
  if (projectId) {
    formData.append('projectId', projectId);
  }
  
  // Note: This will need to be implemented on the backend
  const response = await axios.post(`${API_BASE_URL}/thumbnail/upload-image`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      ...getAuthHeadersForFormData(),
    },
  });
  
  return response.data.data;
};/**
 * Upload extracted frame image for thumbnail editing
 */
export const uploadExtractedFrameImage = async (
  imageData: string,
  options?: { 
    projectId?: string;
    title?: string;
    timestamp?: number;
  }
): Promise<ThumbnailGenerationResult> => {
  // Convert base64 to blob
  const response = await fetch(imageData);
  const blob = await response.blob();
  
  // Create file from blob
  const file = new File([blob], `extracted_frame_${Date.now()}.png`, { type: 'image/png' });
  
  const formData = new FormData();
  formData.append('image', file);
  formData.append('method', 'upload');
  
  // Add all required fields to form data (as strings for FormData)
  if (options?.projectId) {
    formData.append('projectId', options.projectId);
  }
  
  // Ensure title is always provided as a non-empty string
  const title = options?.title && options.title.trim() 
    ? options.title.trim() 
    : `Video Frame at ${Math.floor((options?.timestamp || 0) / 60)}:${Math.floor((options?.timestamp || 0) % 60).toString().padStart(2, '0')}`;
  formData.append('title', title);
  
  // Add other form fields
  formData.append('width', '1536');
  formData.append('height', '864');
  formData.append('aspectRatio', '16:9');

  console.log('📤 [uploadExtractedFrameImage] Sending request to:', `${API_BASE_URL}/thumbnail/generate`);
  console.log('📦 [uploadExtractedFrameImage] FormData contents:');
  for (let [key, value] of formData.entries()) {
    if (key === 'image') {
      console.log(`  ${key}: File (${(value as File).name}, ${(value as File).size} bytes)`);
    } else {
      console.log(`  ${key}: ${value}`);
    }
  }

  const apiResponse = await axios.post(`${API_BASE_URL}/thumbnail/generate`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
      ...getAuthHeadersForFormData(),
    },
  });

  console.log('📡 [uploadExtractedFrameImage] API Response:', apiResponse.data);
  return apiResponse.data.data;
};

/**
 * Get available thumbnail generation options for a project
 */
export const getThumbnailOptions = async (projectId: string) => {
  const response = await axios.get(`${API_BASE_URL}/thumbnail/options/${projectId}`);
  return response.data;
};

/**
 * Fetch projects that have video URLs (for frame extraction)
 */
export const fetchProjectsWithVideos = async () => {
  console.log('Fetching projects with videos...');
  const response = await axios.get(`${API_BASE_URL}/projects?page=1&limit=1000`, {
    headers: getAuthHeaders()
  });
  
  console.log('Projects API response:', response.data);
  
  if (response.data && response.data.projects) {
    // Filter projects that have video URLs AND are not currently processing
    const projectsWithVideos = response.data.projects.filter((project: any) => {
      const hasVideo = project.originalVideoUrl || project.generatedVideoUrl;
      const isProcessing = project.status === 'processing' || project.status === 'uploading' || project.status === 'converting';
      const isCompleted = project.status === 'completed' || project.status === 'ready' || !project.status; // No status usually means completed
      
      // Log project details for debugging
      console.log(`Project: ${project.title}`, {
        id: project.id,
        hasVideo: !!hasVideo,
        status: project.status,
        isProcessing,
        isCompleted,
        willInclude: hasVideo && !isProcessing
      });
      
      // Include projects that have video AND are not processing
      return hasVideo && !isProcessing;
    });
    
    console.log(`Found ${projectsWithVideos.length} completed projects with videos out of ${response.data.projects.length} total projects`);
    return projectsWithVideos;
  }
  
  console.log('No projects data in response');
  return [];
};

/**
 * Check thumbnail generation status
 */
export const checkThumbnailStatus = async (projectId: string) => {
  const response = await axios.get(`${API_BASE_URL}/thumbnail/status/${projectId}`);
  return response.data;
};